import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Order, LinkedOrder } from 'models/order';
import { Alert, Box, Button, Divider, IconButton, List, ListItem, ListItemButton, ListItemText } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { permissions } from 'utils/permission';
import { currentUserVar } from 'apollo/reactive-variables/user';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import AddTrackingLink from 'components/orders/order-details/add-tracking-link-dialog';
import { trackingLinkTemplate } from 'apollo/templates/order';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useMutation } from '@apollo/client';
import InfoText from 'components/shared/info-text.component';
import { isCustomer } from 'utils/client-settings';
import { currentOrgVar } from 'apollo/reactive-variables/organization';

type OrderLinksProps = {
    order: Order;
};
const OrderLinks: FC<OrderLinksProps> = ({ order }: OrderLinksProps) => {
    const { t: translate } = useTranslation(['orders']);
    const navigate = useNavigate();
    const currentUser = currentUserVar();
    const currentOrganization = currentOrgVar();
    const [confirmDeletion, setConfirmDeletion] = useState(false);
    const [trackingLink, setTrackingLink] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();
    const handleCloseDialog = () => {
        setConfirmDeletion(false);
        setTrackingLink(false);
    };

    const isLinkedOrders = () => {
        return order.linkedOrders?.length;
    };

    const navigateToOrderDetails = (orderId: string) => {
        navigate(`/order-details/${orderId}/main-view`);
    };

    const navigateToInvoiceSummary = (invoiceId: string) => {
        navigate(`/invoices/${invoiceId}/summary`);
    };

    const [changeStatus] = useMutation(trackingLinkTemplate, {
        onCompleted: () => {
            handleCloseDialog();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const containsTrackingLink = () => {
        return !!order.transportTrackingLink;
    };

    const openTransportTrackingLink = () => {
        if (order.transportTrackingLink) {
            window.open(order.transportTrackingLink, '_blank');
        }
    };

    const handleChangeStatus = async () => {
        await changeStatus({
            variables: {
                orderId: order?._id,
                transportTrackingLink: '',
            },
        });
    };

    return (
        <Fragment>
            <List disablePadding dense>
                {isLinkedOrders()
                    ? order.linkedOrders?.map((linkedOrder: LinkedOrder) => {
                          return (
                              <ListItem
                                  key={linkedOrder.orderId}
                                  onClick={() => navigateToOrderDetails(linkedOrder.order as string)}
                                  disablePadding
                              >
                                  <ListItemButton data-test="order-links.linked-order">
                                      <ListItemText
                                          sx={{ color: 'primary.main' }}
                                          primary={`${translate('orders:ORDER')} ${linkedOrder.orderId}`}
                                      />
                                  </ListItemButton>
                              </ListItem>
                          );
                      })
                    : undefined}

                {order.isWarrantyOrder && order.orderRef && (
                    <ListItem
                        onClick={() => navigateToOrderDetails(order.orderRef?.order as string)}
                        disablePadding
                        dense
                    >
                        <ListItemButton data-test="order-links.warranty-order">
                            <ListItemText
                                sx={{ color: 'primary.main' }}
                                primary={`${translate('orders:WARRANTY_ORDER')} ${order.orderRef?.orderId}`}
                            />
                        </ListItemButton>
                    </ListItem>
                )}

                {order.isCopyOrder && order.orderRef && (
                    <ListItem
                        onClick={() => navigateToOrderDetails(order.orderRef?.order as string)}
                        disablePadding
                        dense
                    >
                        <ListItemButton data-test="order-links.related-order">
                            <ListItemText
                                sx={{ color: 'primary.main' }}
                                primary={`${translate('orders:RELATED_ORDER_FOR')} ${order.orderRef?.orderId}`}
                            />
                        </ListItemButton>
                    </ListItem>
                )}

                {/* DD-3484: the sub-orders this lab created from the order. The backend resolves
                    them only for the parent's supplier, so rendering them here leaks nothing. */}
                {order.subOrders?.map((subOrder) => (
                    <ListItem
                        key={String(subOrder._id)}
                        onClick={() => navigateToOrderDetails(subOrder._id as string)}
                        disablePadding
                        dense
                    >
                        <ListItemButton data-test="order-links.sub-order">
                            <ListItemText
                                sx={{ color: 'primary.main' }}
                                primary={`${translate('orders:SUB_ORDER')} ${subOrder.orderId}`}
                            />
                        </ListItemButton>
                    </ListItem>
                ))}

                {/* DD-3482: the order this sub-order was created to fulfil. Resolved only for
                    the lab that created the sub-order — never for the subcontractor. */}
                {order.parentOrder && (
                    <ListItem
                        onClick={() => navigateToOrderDetails(order.parentOrder?._id as string)}
                        disablePadding
                        dense
                    >
                        <ListItemButton data-test="order-links.parent-order">
                            <ListItemText
                                sx={{ color: 'primary.main' }}
                                primary={`${translate('orders:PARENT_ORDER')} ${order.parentOrder.orderId}`}
                            />
                        </ListItemButton>
                    </ListItem>
                )}

                {order.invoice && (
                    <ListItem
                        onClick={() => navigateToInvoiceSummary(order.invoice._id as string)}
                        disablePadding
                        dense
                    >
                        <ListItemButton data-test="order-links.invoice">
                            <ListItemText
                                sx={{ color: 'primary.main' }}
                                primary={`${translate('orders:INVOICE')} ${order.invoice.invoiceNumber}`}
                            />
                        </ListItemButton>
                    </ListItem>
                )}

                {order.transportTrackingLink && (
                    <ListItem
                        disablePadding
                        dense
                        secondaryAction={
                            !isCustomer(currentOrganization._id, order.customer._id) ? (
                                <IconButton
                                    edge="end"
                                    size="small"
                                    onClick={() => {
                                        setConfirmDeletion(true);
                                    }}
                                    data-test="order-links.delete-tracking-link-btn"
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            ) : null
                        }
                    >
                        <ListItemButton onClick={openTransportTrackingLink} data-test="order-links.tracking-link">
                            <ListItemText
                                sx={{ color: 'primary.main' }}
                                primary={translate('orders:TRANSPORT_TRACKING_LINK')}
                            />
                        </ListItemButton>
                    </ListItem>
                )}

                {!order.transportTrackingLink &&
                !order.invoice &&
                !order.isWarrantyOrder &&
                !order.orderRef &&
                !order.subOrders?.length &&
                !order.parentOrder &&
                !isLinkedOrders() ? (
                    <InfoText text={translate('orders:NO_LINKS')} />
                ) : undefined}
            </List>

            {!isCustomer(currentOrganization._id, order.customer._id) ? (
                <Fragment>
                    <Box mt={1} mb={1}>
                        <Divider />
                    </Box>
                    <Box>
                        <Button
                            disabled={containsTrackingLink()}
                            onClick={() => {
                                setTrackingLink(true);
                            }}
                            fullWidth
                            size="small"
                            data-test="order-links.add-tracking-link-btn"
                        >
                            {translate('ADD_TRANSPORT_TRACKING_LINK')}
                        </Button>
                    </Box>
                </Fragment>
            ) : undefined}

            <ConfirmDialog
                open={confirmDeletion}
                handleConfirm={handleChangeStatus}
                handleClose={handleCloseDialog}
                dialogTitle={translate('orders:CONFIRM_TITLE')}
                dialogContent={translate('orders:CONFIRM_DELETION')}
            />
            <AddTrackingLink open={trackingLink} onClose={handleCloseDialog} order={order} />
        </Fragment>
    );
};

export default OrderLinks;
